// src/app.ts
// Top-level state machine. Owns the GameState, drives view transitions,
// persists round-by-round to localStorage, computes streak math, handles
// the share path, and wires Clarity events.

import type { GameRecord, GameState, Hsl, Puzzle, RoundResult, ChallengeContext } from './types';
import { puzzleForDate } from './core/puzzle';
import { hslToLab } from './core/color';
import { deltaE2000, scoreFromDeltaE, channelErrors } from './core/score';
import { save } from './core/storage';
import { renderRound, renderBetween } from './views/round';
import { renderResults, showToast, disposeResults } from './views/results';
import { renderStats } from './views/stats';
import { renderArchiveList } from './views/archive';
import { renderAbout } from './views/about';
import { isoMinusDays, isArchiveUnlocked, isValidArchiveDate } from './core/date';
import { dayNameFor } from './core/dayName';
import { event as clarityEvent, tag as clarityTag } from './ui/clarity';
import { renderShareCard, shareWithImage, browserShareDeps } from './ui/shareImage';
import type { ShareCardInput } from './core/shareCard';

const DEFAULT_GUESS: Hsl = { h: 180, s: 50, l: 50 };

export class App {
  private state: GameState;
  private rec: GameRecord;
  private todayIso: string;
  private puzzle: Puzzle;
  private root: HTMLElement;
  private challenge: ChallengeContext | null;
  private preAboutState: GameState | null = null;
  private shareInFlight = false;
  private challengeInFlight = false;
  // Share card PNG, encoded eagerly when the results view mounts so the share
  // tap does not spend its transient user activation waiting on canvas encode
  // (WebKit revokes activation across a slow await, breaking navigator.share).
  private pendingCard: Promise<Blob | null> = Promise.resolve(null);
  // `${dayNumber}:${total}` of the card in `pendingCard`, so re-entering the
  // results view (from stats / archive / about) reuses the encoded PNG.
  private pendingCardKey: string | null = null;

  constructor(root: HTMLElement, todayIso: string, rec: GameRecord) {
    this.root = root;
    this.todayIso = todayIso;
    this.rec = rec;
    this.puzzle = puzzleForDate(todayIso);
    this.challenge =
      rec.challenge && rec.challenge.day === this.puzzle.dayNumber ? rec.challenge : null;

    // Event delegation: clicking the header "about" link navigates to About.
    this.root.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('.header-about') && this.state.phase !== 'about') {
        this.preAboutState = this.state;
        this.transitionTo({ phase: 'about' });
      }
    });

    const partial = (rec.todayResults ?? []).length;
    const playedToday = rec.lastPlayed === todayIso && rec.todayResults && rec.todayTotal !== undefined;
    const stalePartial =
      partial > 0 && rec.lastPlayed !== null && rec.lastPlayed !== todayIso && !playedToday;

    if (playedToday) {
      this.state = {
        phase: 'results',
        puzzle: this.puzzle,
        results: rec.todayResults!,
        total: rec.todayTotal!,
      };
    } else if (stalePartial) {
      this.rec = { ...rec, todayResults: undefined, todayTotal: undefined };
      save(this.rec);
      this.state = this.freshPlayState();
    } else if (partial > 0 && partial < 5) {
      this.state = {
        phase: 'play',
        puzzle: this.puzzle,
        roundIdx: partial as 0|1|2|3|4,
        current: { ...DEFAULT_GUESS },
        results: rec.todayResults!,
      };
    } else {
      this.state = this.freshPlayState();
    }
  }

  private freshPlayState(): GameState {
    return {
      phase: 'play',
      puzzle: this.puzzle,
      roundIdx: 0,
      current: { ...DEFAULT_GUESS },
      results: [],
    };
  }

  start(): void {
    clarityEvent('game_started');
    this.render();
    this.injectHeaderAbout();
  }

  private render(): void {
    const s = this.state;

    switch (s.phase) {
      case 'play':
        renderRound(this.root, {
          puzzle: s.puzzle,
          roundIdx: s.roundIdx,
          initial: s.current,
          showHint: s.roundIdx === 0 && this.rec.gamesPlayed === 0 && !s.archiveIso,
          challenge: !s.archiveIso ? this.challenge : null,
          onSubmit: (guess) => this.submitGuess(guess),
        });
        return;
      case 'between': {
        const last = s.results[s.results.length - 1];
        renderBetween(this.root, {
          puzzle: s.puzzle,
          result: last,
          roundsDone: s.results.length,
          isLast: s.results.length === 5,
          onNext: () => this.advanceFromBetween(),
        });
        return;
      }
      case 'results': {
        this.shareInFlight = false;
        this.challengeInFlight = false;
        if (!s.archiveIso) {
          const cardKey = `${s.puzzle.dayNumber}:${s.total}`;
          if (cardKey !== this.pendingCardKey) {
            const card: ShareCardInput = {
              dayNumber: s.puzzle.dayNumber,
              dayName: dayNameFor(s.puzzle),
              total: s.total,
              streak: this.rec.currentStreak,
              results: s.results,
            };
            this.pendingCardKey = cardKey;
            this.pendingCard = renderShareCard(card).catch((err) => {
              console.warn('[share]', 'share card render failed; sharing text only', err);
              return null;
            });
          }
        }
        renderResults(this.root, {
          puzzle: s.puzzle,
          results: s.results,
          total: s.total,
          streak: this.rec.currentStreak,
          archiveIso: s.archiveIso,
          challenge: !s.archiveIso ? this.challenge : null,
          onShare: (text) => this.share(text),
          onChallenge: (url) => this.shareChallenge(url),
          onViewStats: () => this.transitionTo({ phase: 'stats' }),
          onViewArchive: this.isTodayComplete()
            ? () => this.transitionTo({ phase: 'archive-list' })
            : undefined,
          onBackToArchive: s.archiveIso
            ? () => this.transitionTo({ phase: 'archive-list' })
            : undefined,
          onRate: !s.archiveIso ? (rating: number) => this.ratePuzzle(rating) : undefined,
          onFeedback: !s.archiveIso ? (text: string) => this.submitFeedback(text) : undefined,
          alreadyRated: !s.archiveIso ? !!this.rec.puzzleRatings?.[this.todayIso] : undefined,
        });
        return;
      }
      case 'stats':
        renderStats(this.root, {
          rec: this.rec,
          onBack: () => {
            if (this.rec.lastPlayed === this.todayIso && this.rec.todayResults && this.rec.todayTotal !== undefined) {
              this.transitionTo({
                phase: 'results',
                puzzle: this.puzzle,
                results: this.rec.todayResults,
                total: this.rec.todayTotal,
              });
            } else {
              this.transitionTo({
                phase: 'play',
                puzzle: this.puzzle,
                roundIdx: 0,
                current: { ...DEFAULT_GUESS },
                results: [],
              });
            }
          },
        });
        return;
      case 'archive-list':
        renderArchiveList(this.root, {
          todayIso: this.todayIso,
          archiveResults: this.rec.archiveResults ?? {},
          onPlay: (iso) => this.startArchiveGame(iso),
          onBack: () => {
            this.transitionTo({
              phase: 'results',
              puzzle: this.puzzle,
              results: this.rec.todayResults!,
              total: this.rec.todayTotal!,
            });
          },
        });
        return;
      case 'about':
        renderAbout(this.root, {
          onBack: () => {
            const prev = this.preAboutState;
            this.preAboutState = null;
            if (prev && prev.phase !== 'about') {
              this.transitionTo(prev);
            } else {
              // Fallback: go to play or results depending on today's state
              if (this.rec.lastPlayed === this.todayIso && this.rec.todayResults && this.rec.todayTotal !== undefined) {
                this.transitionTo({
                  phase: 'results',
                  puzzle: this.puzzle,
                  results: this.rec.todayResults,
                  total: this.rec.todayTotal,
                });
              } else {
                this.transitionTo(this.freshPlayState());
              }
            }
          },
        });
        return;
    }
  }

  private transitionTo(next: GameState): void {
    if (this.state.phase === 'results' && next.phase !== 'results') {
      disposeResults();
    }
    this.state = next;
    this.render();
    this.injectHeaderAbout();
  }

  private injectHeaderAbout(): void {
    if (this.state.phase === 'about') return;
    const brand = this.root.querySelector('.brand');
    if (!brand || brand.parentElement?.querySelector('.header-about')) return;
    const sep = document.createElement('span');
    sep.className = 'header-about-sep';
    sep.textContent = '|';
    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'header-about';
    link.textContent = 'about';
    brand.after(sep, link);
  }

  private submitGuess(guess: Hsl): void {
    if (this.state.phase !== 'play') return;
    const activePuzzle = this.state.puzzle;
    const archiveIso = this.state.archiveIso;
    const target = activePuzzle.targets[this.state.roundIdx];
    const labT = hslToLab(target);
    const labG = hslToLab(guess);
    const de = deltaE2000(labT, labG);
    const score = scoreFromDeltaE(de);
    const errors = channelErrors(target, guess);
    const result: RoundResult = { target, guess, score, channelErrors: errors };
    const results = [...this.state.results, result];

    if (!archiveIso) {
      this.rec = { ...this.rec, todayResults: results };
      save(this.rec);
    }

    clarityEvent('round_completed');

    if (results.length === 5) {
      this.finishGame(results, activePuzzle, archiveIso);
      return;
    }

    this.transitionTo({
      phase: 'between',
      puzzle: activePuzzle,
      roundIdx: this.state.roundIdx,
      results,
      archiveIso,
    });
  }

  private advanceFromBetween(): void {
    if (this.state.phase !== 'between') return;
    const { puzzle: activePuzzle, roundIdx, results, archiveIso } = this.state;
    if (results.length === 5) {
      this.finishGame(results, activePuzzle, archiveIso);
    } else {
      this.transitionTo({
        phase: 'play',
        puzzle: activePuzzle,
        roundIdx: (roundIdx + 1) as 0|1|2|3|4,
        current: { ...DEFAULT_GUESS },
        results,
        archiveIso,
      });
    }
  }

  private finishGame(results: RoundResult[], activePuzzle: Puzzle, archiveIso?: string): void {
    const total = Math.round(results.reduce((sum, r) => sum + r.score, 0) / 5);

    const sumDelta = results.reduce(
      (acc, r) => ({
        hue: acc.hue + r.channelErrors.hue,
        sat: acc.sat + r.channelErrors.sat,
        light: acc.light + r.channelErrors.light,
      }),
      { hue: 0, sat: 0, light: 0 }
    );

    if (archiveIso) {
      const existing = this.rec.archiveResults?.[archiveIso];
      const isFirstPlay = !existing;
      const isBetter = isFirstPlay || total > existing.total;
      const archiveResults = { ...(this.rec.archiveResults ?? {}) };
      if (isBetter) {
        archiveResults[archiveIso] = { results, total };
      }
      // Only update profile stats on first play or better score to avoid
      // inflating gamesPlayed / perception profile on repeat attempts.
      if (isFirstPlay || isBetter) {
        this.rec = {
          ...this.rec,
          gamesPlayed: this.rec.gamesPlayed + 1,
          scoreSum: this.rec.scoreSum + total,
          channelErrSum: {
            hue: this.rec.channelErrSum.hue + sumDelta.hue,
            sat: this.rec.channelErrSum.sat + sumDelta.sat,
            light: this.rec.channelErrSum.light + sumDelta.light,
          },
          channelErrCount: this.rec.channelErrCount + 5,
          archiveResults,
        };
      } else {
        this.rec = { ...this.rec, archiveResults };
      }
      save(this.rec);

      clarityEvent('archive_game_finished');
      clarityTag('archive_score', total);

      this.transitionTo({
        phase: 'results',
        puzzle: activePuzzle,
        results,
        total,
        archiveIso,
      });
      return;
    }

    const yesterdayIso = isoMinusDays(this.todayIso, 1);
    let currentStreak: number;
    if (this.rec.lastPlayed === yesterdayIso) currentStreak = this.rec.currentStreak + 1;
    else if (this.rec.lastPlayed === this.todayIso) currentStreak = this.rec.currentStreak;
    else currentStreak = 1;
    const maxStreak = Math.max(this.rec.maxStreak, currentStreak);

    this.rec = {
      ...this.rec,
      installedAt: this.rec.installedAt || this.todayIso,
      lastPlayed: this.todayIso,
      currentStreak,
      maxStreak,
      gamesPlayed: this.rec.gamesPlayed + 1,
      scoreSum: this.rec.scoreSum + total,
      channelErrSum: {
        hue: this.rec.channelErrSum.hue + sumDelta.hue,
        sat: this.rec.channelErrSum.sat + sumDelta.sat,
        light: this.rec.channelErrSum.light + sumDelta.light,
      },
      channelErrCount: this.rec.channelErrCount + 5,
      todayResults: results,
      todayTotal: total,
    };
    save(this.rec);

    clarityEvent('game_finished');
    clarityTag('score', total);
    clarityTag('streak', currentStreak);
    if (this.challenge) {
      const outcome = total > this.challenge.score ? 'won' : total < this.challenge.score ? 'lost' : 'tied';
      clarityTag('challenge_result', outcome);
    }

    this.transitionTo({
      phase: 'results',
      puzzle: activePuzzle,
      results,
      total,
    });
  }

  private async shareChallenge(url: string): Promise<void> {
    if (this.state.phase !== 'results') return;
    // A second tap while the share sheet is open would make navigator.share
    // reject with InvalidStateError and toast the clipboard fallback over it.
    if (this.challengeInFlight) return;
    this.challengeInFlight = true;
    clarityEvent('challenge_clicked');
    const text = `I scored ${this.state.total}/100 on Barve \u2116${this.state.puzzle.dayNumber}. Beat me: ${url}`;
    try {
      if (typeof navigator.share === 'function') {
        try {
          await navigator.share({ text });
          return;
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') return;
          console.warn('[challenge]', 'navigator.share failed; falling back to clipboard', err);
        }
      }
      try {
        await navigator.clipboard.writeText(text);
        showToast('Challenge link copied \u2713');
      } catch (err) {
        console.warn('[challenge]', 'clipboard write failed', err);
        showToast('Could not copy \u2014 long-press to copy manually');
      }
    } finally {
      this.challengeInFlight = false;
    }
  }

  private async share(text: string): Promise<void> {
    if (this.shareInFlight) return;
    this.shareInFlight = true;
    clarityEvent('share_clicked');
    try {
      // Settled at results mount, so this await yields within the same task
      // and the share call below still holds the tap's user activation.
      const blob = await this.pendingCard;
      const method = await shareWithImage(text, blob, browserShareDeps(showToast));
      clarityTag('share_method', method);
    } catch (err) {
      console.warn('[share]', 'share failed unexpectedly', err);
      showToast('Could not share — long-press the share text');
    } finally {
      this.shareInFlight = false;
    }
  }

  private isTodayComplete(): boolean {
    return isArchiveUnlocked(
      this.rec.lastPlayed,
      this.todayIso,
      (this.rec.todayResults ?? []).length,
      this.rec.todayTotal,
    );
  }

  private startArchiveGame(iso: string): void {
    if (!this.isTodayComplete()) return;
    if (!isValidArchiveDate(iso, this.todayIso)) return;
    const archivePuzzle = puzzleForDate(iso);
    this.transitionTo({
      phase: 'play',
      puzzle: archivePuzzle,
      roundIdx: 0,
      current: { ...DEFAULT_GUESS },
      results: [],
      archiveIso: iso,
    });
  }

  private ratePuzzle(rating: number): void {
    const ratings = { ...(this.rec.puzzleRatings ?? {}) };
    ratings[this.todayIso] = rating;
    this.rec = { ...this.rec, puzzleRatings: ratings };
    save(this.rec);
    clarityEvent('puzzle_rated');
    const label = rating === 1 ? 'loved' : rating === 2 ? 'good' : 'meh';
    clarityTag('puzzle_rating', label);
  }

  private submitFeedback(text: string): void {
    clarityEvent('puzzle_feedback');
    clarityTag('puzzle_feedback', text.slice(0, 140));
  }
}
